import { useState, useEffect, useCallback } from "react";
import { formatDuration } from "@tellescope/utils";

export interface CallTimerProps {
  /** When the call started (null if not started yet) */
  startTime: Date | null;
  /** Additional className for the timer container */
  className?: string;
}

/**
 * CallTimer - Displays the duration of an active call.
 *
 * Automatically updates every second while the call is active.
 * Shows "0s" when no start time is provided or call just started.
 *
 * @example
 * ```tsx
 * <CallTimer startTime={callStartTime} />
 * ```
 */
export function CallTimer({ startTime, className }: CallTimerProps) {
  const [duration, setDuration] = useState(0);

  const calculateDuration = useCallback(() => {
    if (!startTime) return 0;
    return Math.floor((Date.now() - startTime.getTime()) / 1000);
  }, [startTime]);

  useEffect(() => {
    if (!startTime) {
      setDuration(0);
      return;
    }

    // Initial calculation
    setDuration(calculateDuration());

    // Update every second
    const interval = setInterval(() => {
      setDuration(calculateDuration());
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, calculateDuration]);

  return <span className={className}>{formatDuration(duration, "seconds")}</span>;
}
